import type { CSSProperties, ReactNode } from "react";

import { Checkbox } from "./Checkbox";
import "./Table.css";

export type Column<T> = {
  id: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  width?: number | string;
  align?: "left" | "right" | "center";
  className?: string;
};

type Props<T> = {
  rows: T[];
  columns: Column<T>[];
  getKey: (row: T) => string;
  selection?: {
    selected: Set<string>;
    toggle: (key: string) => void;
    toggleAll: () => void;
    allSelected: boolean;
  };
  onRowClick?: (row: T) => void;
  rowAction?: (row: T) => ReactNode;
  empty?: ReactNode;
  density?: "compact" | "comfortable";
  className?: string;
};

export function Table<T>({
  rows,
  columns,
  getKey,
  selection,
  onRowClick,
  rowAction,
  empty,
  density = "comfortable",
  className = "",
}: Props<T>) {
  const tableCls = `emp-table ${density === "compact" ? "is-compact" : ""} ${className}`.trim();
  return (
    <div className="emp-table-card">
      <table className={tableCls}>
        <thead>
          <tr>
            {selection && (
              <th className="col-chk">
                <Checkbox checked={selection.allSelected} onChange={() => selection.toggleAll()} />
              </th>
            )}
            {columns.map((c) => {
              const style: CSSProperties = {};
              if (c.width != null) style.width = c.width;
              if (c.align) style.textAlign = c.align;
              return (
                <th key={c.id} style={style} className={c.className}>
                  {c.header}
                </th>
              );
            })}
            {rowAction && <th className="col-action" />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && empty && (
            <tr>
              <td colSpan={columns.length + (selection ? 1 : 0) + (rowAction ? 1 : 0)}>{empty}</td>
            </tr>
          )}
          {rows.map((row, idx) => {
            const key = getKey(row);
            const isSel = selection?.selected.has(key);
            return (
              <tr
                key={key}
                className={isSel ? "selected" : ""}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{ cursor: onRowClick ? "pointer" : undefined }}
              >
                {selection && (
                  <td className="col-chk">
                    <Checkbox checked={!!isSel} onChange={() => selection.toggle(key)} />
                  </td>
                )}
                {columns.map((c) => {
                  const style: CSSProperties = c.align ? { textAlign: c.align } : {};
                  return (
                    <td key={c.id} style={style} className={c.className}>
                      {c.cell(row, idx)}
                    </td>
                  );
                })}
                {rowAction && (
                  <td className="col-action" onClick={(e) => e.stopPropagation()}>
                    {rowAction(row)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
